"use client"

import { useState, useEffect } from "react"
import styled, { keyframes, css } from "styled-components"
import { FileText, CheckCircle, AlertCircle } from "lucide-react"

interface PDFLoaderProps {
  fileName?: string
  progress?: number
  status?: "downloading" | "rendering" | "ready" | "error"
  fileSize?: string
  onReady?: () => void
  onError?: (error: string) => void
}

// Keyframes para animaciones
const bounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
`

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.3;
  }
`

// Styled Components
const Container = styled.div`
  width: 100%;
  max-width: 28rem;
  margin: 0 auto;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #f3f4f6;
  padding: 2rem;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
`

const IconContainer = styled.div`
  position: relative;
`

const PulseIcon = styled.div<{ $isLoading: boolean }>`
  position: absolute;
  inset: 0;
  ${(props) =>
    props.$isLoading &&
    css`
    animation: ${pulse} 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  `}
`

const TextContainer = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Title = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0;
`

const FileInfoContainer = styled.div`
  background-color: #f9fafb;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  width: 100%;
`

const FileInfoContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const FileInfoLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const FileName = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FileSize = styled.span`
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
`

const ProgressContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ProgressHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ProgressLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
`

const ProgressPercentage = styled.span<{ $color: string }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${(props) => props.$color};
`

const ProgressBarContainer = styled.div`
  width: 100%;
  background-color: #e5e7eb;
  border-radius: 9999px;
  height: 0.5rem;
  overflow: hidden;
`

const ProgressBarFill = styled.div<{ $width: number; $color: string }>`
  height: 100%;
  background-color: ${(props) => props.$color};
  transition: all 300ms cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 9999px;
  width: ${(props) => props.$width}%;
`

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
`

const DotsContainer = styled.div`
  display: flex;
  gap: 0.25rem;
`

const Dot = styled.div<{ $delay: string }>`
  width: 0.5rem;
  height: 0.5rem;
  background-color: #818cf8;
  border-radius: 50%;
  animation: ${bounce} 1.4s ease-in-out infinite both;
  animation-delay: ${(props) => props.$delay};
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  width: 100%;
`

const Button = styled.button<{ $variant?: "primary" | "secondary" }>`
  font-weight: 500;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  transition: all 200ms;
  border: none;
  cursor: pointer;
  
  ${(props) =>
    props.$variant === "secondary"
      ? css`
    flex: 1;
    background-color: #f3f4f6;
    color: #374151;
    
    &:hover {
      background-color: #e5e7eb;
    }
  `
      : css`
    ${props.$variant === "primary" ? "flex: 1;" : "width: 100%;"}
    background-color: #4f46e5;
    color: white;
    
    &:hover {
      background-color: #4338ca;
    }
  `}
`

export default function PDFLoader({
  fileName = "Cañeria.pdf",
  progress = 0,
  status = "downloading",
  fileSize = "2.4 MB",
  onReady,
  onError,
}: PDFLoaderProps) {
  const [currentStatus, setCurrentStatus] = useState<"downloading" | "rendering" | "ready" | "error">(status)

  // Simulación de progreso para demo
  useEffect(() => {
    if (currentStatus === "downloading" && progress < 70) {
      const timer = setTimeout(
        () => {
          const newProgress = Math.min(progress + Math.random() * 10, 70)
          // setprogress(newProgress)

          if (newProgress >= 70) {
            setCurrentStatus("rendering")
          }
        },
        150 + Math.random() * 200,
      )

      return () => clearTimeout(timer)
    } else if (currentStatus === "rendering" && progress < 100) {
      const timer = setTimeout(
        () => {
          const newProgress = Math.min(progress + Math.random() * 8, 100)
          // setprogress(newProgress)

          if (newProgress >= 100) {
            setCurrentStatus("ready")
            onReady?.()
          }
        },
        100 + Math.random() * 150,
      )

      return () => clearTimeout(timer)
    }
  }, [progress, currentStatus, onReady])

  const getStatusConfig = () => {
    switch (currentStatus) {
      case "rendering":
        return {
          title: "Preparando vista previa",
          subtitle: "Renderizando páginas del documento...",
          icon: <FileText className="w-8 h-8 text-indigo-500" />,
          progressColor: "#6366f1",
          textColor: "#4f46e5",
          actionText: "Renderizando...",
        }
      case "ready":
        return {
          title: "Documento listo",
          subtitle: "El PDF está disponible para visualizar",
          icon: <CheckCircle className="w-8 h-8 text-green-500" />,
          progressColor: "#10b981",
          textColor: "#059669",
          actionText: "Completado",
        }
      case "error":
        return {
          title: "Error al cargar el documento",
          subtitle: "No se pudo descargar o procesar el archivo",
          icon: <AlertCircle className="w-8 h-8 text-red-500" />,
          progressColor: "#ef4444",
          textColor: "#dc2626",
          actionText: "Error",
        }
      default: // downloading
        return {
          title: "Descargando documento",
          subtitle: "Obteniendo archivo del servidor...",
          icon: <FileText className="w-8 h-8 text-indigo-500" />,
          progressColor: "#6366f1",
          textColor: "#4f46e5",
          actionText: "Descargando...",
        }
    }
  }

  const config = getStatusConfig()
  const displayProgress = currentStatus === "downloading" || currentStatus === "rendering" ? progress : 100

  return (
    <Container>
      <ContentWrapper>
        {/* Icono principal */}
        <IconContainer>
          {config.icon}
          {currentStatus === "downloading" && (
            <PulseIcon $isLoading={true}>
              <FileText className="w-8 h-8 text-indigo-300" />
            </PulseIcon>
          )}
        </IconContainer>

        {/* Título y subtítulo */}
        <TextContainer>
          <Title>{config.title}</Title>
          <Subtitle>{config.subtitle}</Subtitle>
        </TextContainer>

        {/* Nombre del archivo */}
        {/* <FileInfoContainer>
          <FileInfoContent>
            <FileInfoLeft>
              <FileText className="w-5 h-5 text-gray-400" />
              <FileName>{fileName}</FileName>
            </FileInfoLeft>
            <FileSize>{fileSize}</FileSize>
          </FileInfoContent>
        </FileInfoContainer> */}

        {/* Barra de progreso */}
        <ProgressContainer>
          <ProgressHeader>
            <ProgressLabel>Progreso</ProgressLabel>
            <ProgressPercentage $color={config.textColor}>{Math.round(displayProgress)}%</ProgressPercentage>
          </ProgressHeader>

          <ProgressBarContainer>
            <ProgressBarFill $width={displayProgress} $color={config.progressColor} />
          </ProgressBarContainer>
        </ProgressContainer>

        {/* Indicadores de estado adicionales */}
        {(currentStatus === "downloading" || currentStatus === "rendering") && (
          <StatusIndicator>
            <DotsContainer>
              <Dot $delay="0ms" />
              <Dot $delay="150ms" />
              <Dot $delay="300ms" />
            </DotsContainer>
            <span>{config.actionText}</span>
          </StatusIndicator>
        )}

        {/* Botones de acción */}
        {/* {currentStatus === "ready" && <Button>Ver documento</Button>}

        {currentStatus === "error" && (
          <ButtonContainer>
            <Button $variant="secondary">Cancelar</Button>
            <Button
              $variant="primary"
              onClick={() => {
                setCurrentStatus("downloading")
                setprogress(0)
              }}
            >
              Reintentar
            </Button>
          </ButtonContainer>
        )} */}
      </ContentWrapper>
    </Container>
  )
}
